/**
 * React error boundary for any route that does heavy DOM work (PDF rendering,
 * canvas drawing, etc.) where an uncaught throw would otherwise white-screen
 * the entire app.
 *
 * Without this, a single bad PDF or a pdf.js worker crash takes the whole
 * page down with no recovery path. Catch errors per-route, show a friendly
 * fallback, and offer a reload button. Errors get console.error'd so they
 * still surface in browser devtools + Cloudflare RUM.
 *
 * Usage:
 *   <ErrorBoundary label="PDF mapper">
 *     <DocumentTemplateMapper />
 *   </ErrorBoundary>
 */
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Short name shown in the fallback header — "PDF mapper", "Income projector", etc. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Surface to devtools so Daniel can copy the stack when reporting bugs.
    // Cloudflare's Real User Monitoring (if enabled) picks these up too.
    console.error(
      `[ErrorBoundary${this.props.label ? ` — ${this.props.label}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            maxWidth: 720,
            margin: '60px auto',
            padding: '32px 28px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: 'var(--text-heading)',
              margin: '0 0 8px',
              letterSpacing: '-0.01em',
            }}
          >
            Something went wrong
            {this.props.label ? ` in ${this.props.label}` : ''}.
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.5, margin: '0 0 16px' }}>
            The page hit an error and stopped rendering. Try reloading; if it
            keeps happening, screenshot the details below and send to Daniel.
          </p>
          {/* Surface the error message inline (no click needed) — the whole
              point of catching is so Daniel can read what broke. */}
          <div
            style={{
              background: 'var(--bg-surface-2)',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 13,
              marginBottom: 12,
              fontFamily: 'ui-monospace, monospace',
              color: 'var(--text-heading)',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message || 'Unknown error'}
          </div>
          <details
            style={{
              background: 'var(--bg-surface-2)',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 11,
              marginBottom: 20,
              fontFamily: 'ui-monospace, monospace',
              color: 'var(--text-muted)',
            }}
            open
          >
            <summary style={{ cursor: 'pointer', color: 'var(--text-body)' }}>
              Stack trace
            </summary>
            <pre style={{ whiteSpace: 'pre-wrap', margin: '10px 0 0' }}>
              {this.state.error.stack ?? '(no stack available)'}
            </pre>
          </details>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                background: 'var(--brand-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.reset}
              style={{
                padding: '8px 16px',
                background: 'var(--bg-surface)',
                color: 'var(--text-heading)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontWeight: 500,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
